import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signUp, signInWithGoogle } from "../services/authService";
import {
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  UserPlus,
  Film,
  Globe,
  FileText,
  Upload,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

const CONTENT_TYPES = [
  "Restored Classic Films",
  "Original African Cinema",
  "Documentary",
  "Short Film",
  "Series / TV Show",
  "Cultural Heritage Content",
  "Animation",
  "Experimental / Avant-Garde",
];

const AFRICAN_REGIONS = [
  "West Africa",
  "East Africa",
  "North Africa",
  "Central Africa",
  "Southern Africa",
  "African Diaspora",
];

const CreatorSignUp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // Step 1: Account, Step 2: Creator Profile
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1 - Account
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
    // Step 2 - Creator Profile
    creatorName: "",
    country: "",
    region: "",
    bio: "",
    contentTypes: [],
    website: "",
    acceptedTerms: false,
    acceptedRights: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError("");
  };

  const toggleContentType = (type) => {
    setFormData((prev) => ({
      ...prev,
      contentTypes: prev.contentTypes.includes(type)
        ? prev.contentTypes.filter((t) => t !== type)
        : [...prev.contentTypes, type],
    }));
  };

  const validateStep1 = () => {
    if (formData.displayName.length < 2) {
      setError("Name must be at least 2 characters.");
      return false;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (formData.creatorName.length < 2) {
      setError("Creator/Studio name is required.");
      return false;
    }
    if (!formData.country) {
      setError("Please enter your country.");
      return false;
    }
    if (!formData.region) {
      setError("Please select your African region.");
      return false;
    }
    if (formData.bio.length < 30) {
      setError("Please write a bio of at least 30 characters.");
      return false;
    }
    if (formData.contentTypes.length === 0) {
      setError("Please select at least one content type.");
      return false;
    }
    if (!formData.acceptedTerms || !formData.acceptedRights) {
      setError("Please accept both the terms and rights declaration.");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError("");
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validateStep2()) return;

    setLoading(true);
    try {
      await signUp(formData.email, formData.password, formData.displayName, {
        role: "creator",
        creatorName: formData.creatorName,
        country: formData.country,
        region: formData.region,
        bio: formData.bio,
        contentTypes: formData.contentTypes,
        website: formData.website,
        status: "pending_review", // Creators go through review before publishing
      });
      navigate("/creator-dashboard");
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("This email is already registered. Please sign in.");
      } else if (err.code === "auth/invalid-email") {
        setError("Invalid email address.");
      } else {
        setError("Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithGoogle({ role: "creator" });
      navigate("/creator-dashboard");
    } catch (err) {
      setError("Failed to sign up with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container fade-in-up">
      <div className="auth-content" style={{ maxWidth: "480px" }}>
        {/* Header */}
        <div className="auth-header">
          <div className="auth-logo">🎬</div>
          <h1 className="auth-title">Become a Creator</h1>
          <p className="auth-subtitle">
            Share African heritage cinema with the world
          </p>
        </div>

        {/* Step Indicator */}
        <div style={styles.stepIndicator}>
          <div style={styles.stepRow}>
            <div
              style={{
                ...styles.stepDot,
                ...(step >= 1 ? styles.stepDotActive : {}),
              }}
            >
              1
            </div>
            <div
              style={{
                ...styles.stepLine,
                ...(step >= 2 ? styles.stepLineActive : {}),
              }}
            />
            <div
              style={{
                ...styles.stepDot,
                ...(step >= 2 ? styles.stepDotActive : {}),
              }}
            >
              2
            </div>
          </div>
          <div style={styles.stepLabels}>
            <span
              style={step === 1 ? styles.stepLabelActive : styles.stepLabel}
            >
              Account
            </span>
            <span
              style={step === 2 ? styles.stepLabelActive : styles.stepLabel}
            >
              Creator Profile
            </span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="auth-error">
            <span>⚠️ {error}</span>
          </div>
        )}

        {/* ── STEP 1: Account Details ── */}
        {step === 1 && (
          <div className="auth-form">
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <div className="input-wrapper">
                <User className="input-icon" size={20} />
                <input
                  type="text"
                  name="displayName"
                  className="form-input"
                  placeholder="Your full name"
                  value={formData.displayName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="form-input"
                  placeholder="Min. 6 characters"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  className="form-input"
                  placeholder="Repeat your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="btn-primary auth-btn"
              onClick={handleNext}
            >
              Next: Creator Profile <ChevronRight size={18} />
            </button>

            <div className="auth-divider">
              <span>or continue with</span>
            </div>

            <button
              type="button"
              className="btn-google"
              onClick={handleGoogleSignUp}
              disabled={loading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Sign up with Google
            </button>
          </div>
        )}

        {/* ── STEP 2: Creator Profile ── */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="form-label">
                <Film size={15} style={{ display: "inline", marginRight: 6 }} />
                Creator / Studio Name
              </label>
              <div className="input-wrapper">
                <Film className="input-icon" size={20} />
                <input
                  type="text"
                  name="creatorName"
                  className="form-input"
                  placeholder="Your filmmaker or studio name"
                  value={formData.creatorName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <div className="input-wrapper">
                <Globe className="input-icon" size={20} />
                <input
                  type="text"
                  name="country"
                  className="form-input"
                  placeholder="e.g. Nigeria, Senegal, South Africa"
                  value={formData.country}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">African Region</label>
              <div className="input-wrapper">
                <Globe className="input-icon" size={20} />
                <select
                  name="region"
                  className="form-input"
                  value={formData.region}
                  onChange={handleChange}
                  style={{ paddingLeft: "2.5rem", appearance: "none" }}
                >
                  <option value="">Select your region</option>
                  {AFRICAN_REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Bio / About Your Work</label>
              <div
                className="input-wrapper"
                style={{ alignItems: "flex-start" }}
              >
                <FileText
                  className="input-icon"
                  size={20}
                  style={{ marginTop: 14 }}
                />
                <textarea
                  name="bio"
                  className="form-input"
                  placeholder="Tell us about your filmmaking background and the heritage content you'll share..."
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  style={{ paddingTop: "0.75rem", resize: "none" }}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Content Types You'll Upload</label>
              <div style={styles.tagGrid}>
                {CONTENT_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleContentType(type)}
                    style={{
                      ...styles.tag,
                      ...(formData.contentTypes.includes(type)
                        ? styles.tagActive
                        : {}),
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Website / Portfolio (optional)
              </label>
              <div className="input-wrapper">
                <Globe className="input-icon" size={20} />
                <input
                  type="url"
                  name="website"
                  className="form-input"
                  placeholder="https://yoursite.com"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Rights & Terms */}
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="acceptedRights"
                  checked={formData.acceptedRights}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>
                  I confirm I own the rights or have proper licensing for all
                  content I upload, and it relates to African heritage cinema.
                </span>
              </label>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="acceptedTerms"
                  checked={formData.acceptedTerms}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" className="terms-link">
                    Creator Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="terms-link">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            </div>

            {/* Note about review */}
            <div style={styles.reviewNote}>
              <Upload size={15} />
              <span>
                Your creator account will be reviewed before content goes live.
                You'll be notified within 48 hours.
              </span>
            </div>

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setStep(1)}
                style={{ flex: 1 }}
              >
                <ChevronLeft size={18} /> Back
              </button>
              <button
                type="submit"
                className="btn-primary auth-btn"
                disabled={loading}
                style={{ flex: 2 }}
              >
                {loading ? (
                  <span>⏳ Creating account...</span>
                ) : (
                  <>
                    <UserPlus size={20} /> Create Creator Account
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        <div className="auth-switch">
          <p>
            Already have an account?{" "}
            <Link to="/signin" className="auth-link">
              Sign in
            </Link>
          </p>
          <p style={{ marginTop: "0.5rem" }}>
            Viewer account?{" "}
            <Link to="/signup" className="auth-link">
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  stepIndicator: {
    marginBottom: "1.5rem",
  },
  stepRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    marginBottom: "0.5rem",
  },
  stepDot: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.1)",
    color: "rgba(255,255,255,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "0.875rem",
    border: "2px solid rgba(255,255,255,0.15)",
    transition: "all 0.3s ease",
  },
  stepDotActive: {
    background: "linear-gradient(135deg, #c8963e, #e8b94f)",
    color: "#1a0a00",
    border: "2px solid #e8b94f",
  },
  stepLine: {
    flex: 1,
    height: 2,
    background: "rgba(255,255,255,0.1)",
    maxWidth: 80,
    transition: "all 0.3s ease",
  },
  stepLineActive: {
    background: "linear-gradient(90deg, #c8963e, #e8b94f)",
  },
  stepLabels: {
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: 0,
  },
  stepLabel: {
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
  },
  stepLabelActive: {
    fontSize: "0.75rem",
    color: "#e8b94f",
    fontWeight: 600,
  },
  tagGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.5rem",
    marginTop: "0.5rem",
  },
  tag: {
    padding: "0.4rem 0.85rem",
    borderRadius: "999px",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.05)",
    color: "rgba(255,255,255,0.7)",
    fontSize: "0.78rem",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  tagActive: {
    background: "linear-gradient(135deg, #c8963e33, #e8b94f22)",
    border: "1px solid #e8b94f",
    color: "#e8b94f",
    fontWeight: 600,
  },
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    margin: "0.5rem 0 1rem",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.6rem",
    color: "rgba(255,255,255,0.75)",
    fontSize: "0.82rem",
    lineHeight: 1.5,
    cursor: "pointer",
  },
  checkbox: {
    marginTop: 2,
    accentColor: "#e8b94f",
    flexShrink: 0,
  },
  reviewNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
    background: "rgba(200, 150, 62, 0.1)",
    border: "1px solid rgba(200, 150, 62, 0.3)",
    borderRadius: "8px",
    padding: "0.75rem",
    color: "#e8b94f",
    fontSize: "0.8rem",
    lineHeight: 1.5,
    marginBottom: "1rem",
  },
};

export default CreatorSignUp;
